import { Checkbox, Dropdown, Label, PrimaryButton, Spinner, Stack, TextField } from '@fluentui/react';
import _ from 'lodash';
import * as React from 'react';
import { AGENCY_FLAG_TYPE, AGENCY_PAYMENT_TERM_TYPE } from '../../config/constants';
import { AgencyData, AgencyMetadata, RESOURCE_PERMISSION_TYPES } from '../../redux/concierge/types';

interface AgencyFormProps {
  pristineAgency?: AgencyData;
  isSaving?: boolean;
  saveAgency?: (agency: AgencyMetadata) => void;
}

interface AgencyFormState {
  agency: AgencyMetadata;
}

export class AgencyForm extends React.Component<AgencyFormProps, AgencyFormState> {
  constructor(props: any) {
    super(props);

    let agency;
    if (this.props.pristineAgency) {
      agency = _.cloneDeep(this.props.pristineAgency);
    } else {
      agency = {
        name: '',
        paymentTerms: 'CREDIT_CARD_ONLY',
        defaultPermissionsCreatingAccount: ['ALL'],
        defaultPermissionsCreatingPatient: ['ALL'],
        defaultPermissionsCreatingPayment: ['ALL'],
        defaultPermissionsCreatingAddress: ['ALL'],
        flags: [],
        serviceConfigMap: {},
      };
    }

    this.state = {
      agency: agency,
    };
  }

  public render(): JSX.Element {
    const resourceTypes = ['Account', 'Patient', 'Payment', 'Address'];

    return (
      <div>
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: 700 } }}>
          <TextField
            label="Agency name"
            readOnly={!this.props.saveAgency}
            value={this.state.agency.name}
            placeholder={'Enter name here'}
            multiline={false}
            onChange={(ev, v) => this.setState({ agency: { ...this.state.agency, name: v! } })}
            data-tid="inp_agencyName"
          />
          <TextField
            label="Agency logo URL"
            readOnly={!this.props.saveAgency}
            value={this.state.agency.logoUrl}
            placeholder={'Enter URL here'}
            multiline={false}
            onChange={(ev, v) => this.setState({ agency: { ...this.state.agency, logoUrl: v! } })}
          />
          {this.state.agency.logoUrl && <img src={this.state.agency.logoUrl} alt={'Agency logo'} style={{ border: '1px solid gray' }} width={100} height={100} />}
          <Dropdown
            label="Payment terms"
            placeholder="Select a Payment Term"
            disabled={!this.props.saveAgency}
            selectedKey={this.state.agency.paymentTerms}
            onChange={(ev, v) => v && this.setState({ agency: { ...this.state.agency, paymentTerms: String(v.key)! } })}
            options={AGENCY_PAYMENT_TERM_TYPE}
            data-tid="dd_paymentTerms"
          />
          {this.renderPaymentTermDescription()}
          <div>
            <Label>Default permissions when creating...</Label>
            <div style={{ color: 'gray' }}>
              <i>
                This agency will be granted the following permissions for newly created resources of each respective type. These permissions will not be retroactively applied to
                resources previously created by this agency.
              </i>
            </div>
            <table>
              <tbody>
                <tr>
                  <td></td>
                  {RESOURCE_PERMISSION_TYPES.map((permission) => {
                    return (
                      <td key={permission} style={{ width: 70, textAlign: 'center' }}>
                        {permission}
                      </td>
                    );
                  })}
                </tr>
                {resourceTypes.map((prg) => {
                  const resourceType = prg as 'Account' | 'Patient' | 'Payment' | 'Address';
                  const isAllChecked = this.isPermissionChecked(resourceType, 'ALL');
                  return (
                    <tr key={prg}>
                      <td>...{prg.toLowerCase()}:</td>
                      {RESOURCE_PERMISSION_TYPES.map((permission) => {
                        const isImplicitlyChecked = permission !== 'ALL' && isAllChecked;
                        return (
                          <td key={`${prg}_${permission}`}>
                            <Checkbox
                              disabled={isImplicitlyChecked || !this.props.saveAgency}
                              checked={isImplicitlyChecked || this.isPermissionChecked(resourceType, permission)}
                              onChange={(event, value) => this.setPermissionChecked(resourceType, permission, value === undefined ? false : value)}
                              styles={{ root: { justifyContent: 'center' } }}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div>
            <Label>Flags</Label>
            {AGENCY_FLAG_TYPE.map((flagType) => {
              const agencyFlags = this.state.agency.flags || [];
              const isFlagEnabled = agencyFlags.indexOf(flagType.key) !== -1;
              return (
                <div key={flagType.key} style={{ marginBottom: 10 }}>
                  <Checkbox label={flagType.text} checked={isFlagEnabled} onChange={() => this.updateFlagStatus(flagType.key, !isFlagEnabled)} />
                  <div style={{ color: 'gray' }}>
                    <i>{flagType.description}</i>
                  </div>
                </div>
              );
            })}
          </div>
        </Stack>
        {this.props.saveAgency && (
          <PrimaryButton
            onClick={() => this.props.saveAgency && this.props.saveAgency(this.state.agency)}
            style={{ marginTop: 20 }}
            text="Save"
            disabled={!this.canSubmit() || this.props.isSaving}
            data-tid="btn_save"
          />
        )}

        {this.props.isSaving && <Spinner />}
      </div>
    );
  }

  private isPermissionChecked = (resourceType: 'Account' | 'Patient' | 'Payment' | 'Address', permissionType: string): boolean => {
    const agencyMetadataKey: keyof AgencyMetadata = `defaultPermissionsCreating${resourceType}`;
    return this.state.agency[agencyMetadataKey].indexOf(permissionType) !== -1;
  };

  private setPermissionChecked = (resourceType: 'Account' | 'Patient' | 'Payment' | 'Address', permissionType: string, checked: boolean) => {
    const agencyMetadataKey: keyof AgencyMetadata = `defaultPermissionsCreating${resourceType}`;
    let permissions: string[];

    if (permissionType === 'ALL') {
      if (checked) {
        permissions = ['ALL'];
      } else {
        permissions = [];
      }
    } else {
      permissions = [...this.state.agency[agencyMetadataKey]];
      if (checked) {
        permissions.push(permissionType);
      } else {
        permissions.splice(permissions.indexOf(permissionType), 1);
      }
    }

    this.setState({
      agency: {
        ...this.state.agency,
        [agencyMetadataKey]: permissions,
      },
    });
  };

  private updateFlagStatus(flag: string, shouldEnable: boolean) {
    let flags = this.state.agency.flags?.slice() || [];
    flags = flags.filter((f) => f !== flag);
    if (shouldEnable) {
      flags.push(flag);
    }
    this.setState({ agency: { ...this.state.agency, flags } });
  }

  private canSubmit(): boolean {
    return this.state.agency.name.length > 0 && (!this.props.pristineAgency || JSON.stringify(this.props.pristineAgency) !== JSON.stringify(this.state.agency));
  }

  private renderPaymentTermDescription(): JSX.Element {
    let currentTermType = AGENCY_PAYMENT_TERM_TYPE[0];
    AGENCY_PAYMENT_TERM_TYPE.forEach((termType) => {
      if (this.state.agency.paymentTerms === termType.key) {
        currentTermType = termType;
      }
    });

    return (
      <div style={{ color: 'gray' }}>
        <i>{currentTermType.description}</i>
      </div>
    );
  }
}
